import { ErrorBoundary } from '@sentry/react';
import { ConfirmationDialog, Typography } from '@statsbomb/kitbag-components';
import { useAtomValue, useSetAtom } from 'jotai';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate, useMatch } from 'react-router-dom';
import { trackObjectChangeEvent } from '@/analytics/snowplow/analytics';
import { configTypeContextMap } from '@/analytics/snowplow/types';
import { userConfigsAtom } from '@/atoms/queries/userConfigs';
import { toastDataAtom } from '@/atoms/toast';
import {
  isGenericDeleteModalOpenAtom,
  resetUserConfigActionStateAtom,
  userConfigIdAtom,
  userConfigTypeAtom,
} from '@/atoms/userConfigs';
import { GeneralFallback } from '@/components/contentState/GeneralFallback';
import { useDeleteUserConfig } from '@/hooks/useDeleteUserConfig';
import { useUpdateQueryString } from '@/hooks/useUpdateQueryString';
import { UserConfigType } from '@/types/userConfigs';
import { noop } from '@/utils/noop';

const GenericDeleteModalBase = ({
  configName,
  onConfirm,
  userConfigType,
  feedback,
}: {
  configName?: string | null;
  onConfirm?: () => Promise<void>;
  userConfigType?: UserConfigType;
  feedback?: 'loading' | 'error';
}) => {
  const { t } = useTranslation(['general', 'modal']);
  const [isLoading, setIsLoading] = useState(false);
  const resetUserConfigActionState = useSetAtom(resetUserConfigActionStateAtom);

  return (
    <ConfirmationDialog
      testId="delete-modal"
      id="delete-config-modal"
      title={t('deleteModal.title', {
        ns: 'modal',
        /* handles special characters */
        itemToDelete: `${configName}`,
        interpolation: { escapeValue: false },
      })}
      isOpen
      onConfirm={async () => {
        setIsLoading(true);
        await onConfirm?.();
        setIsLoading(false);
      }}
      onCancel={resetUserConfigActionState}
      cancelLabel={t('cancel')}
      confirmLabel={t('delete')}
      confirmButtonVariant="error"
      isPendingConfirm={isLoading}
      isDisabledConfirm={!!feedback}
    >
      {feedback ? (
        <GeneralFallback type={feedback} />
      ) : (
        <Typography variant="bodyRegular">
          {t('deleteModal.content', {
            ns: 'modal',
            itemType: t(`deleteModal.itemType.${userConfigType}`, { ns: 'modal' }),
          })}
        </Typography>
      )}
    </ConfirmationDialog>
  );
};

const GenericDeleteModalWithData = ({
  configParamKey,
  onSuccess = noop,
}: {
  configParamKey?: string;
  onSuccess?: () => void;
}) => {
  const deleteUserConfig = useDeleteUserConfig();
  const setToastData = useSetAtom(toastDataAtom);

  const userConfigIdToAmend = useAtomValue(userConfigIdAtom);
  const configs = useAtomValue(userConfigsAtom);
  const configToDelete = configs.data.find(config => config.configId === userConfigIdToAmend);
  const configNameToDelete = configToDelete ? (configToDelete.definition as any).titleSettings.title : null;
  const [searchParams] = useSearchParams();
  const updateQueryString = useUpdateQueryString(searchParams);
  const navigate = useNavigate();
  const userConfigType = useAtomValue(userConfigTypeAtom);
  const isPlayerSection = !!useMatch('/:locale/player/*');
  const entity = isPlayerSection ? 'Player' : 'Team';

  // we want to add entity to the context for radar templates.
  const trackingContext =
    userConfigType === UserConfigType.RADAR_TEMPLATE
      ? `${entity} - ${configTypeContextMap[userConfigType]}`
      : configTypeContextMap[userConfigType!];

  const resetGenericDeleteModal = useSetAtom(resetUserConfigActionStateAtom);

  if (!configToDelete) {
    throw new Error('Could not find config for the provided config ID');
  }

  return (
    <GenericDeleteModalBase
      configName={configNameToDelete}
      userConfigType={userConfigType!}
      onConfirm={async () => {
        const isSuccess = await deleteUserConfig(userConfigIdToAmend!);
        setToastData({
          toastName: `deleteConfig.${userConfigType}`,
          type: isSuccess ? 'success' : 'error',
          entityName: configNameToDelete,
        });

        if (isSuccess) {
          trackObjectChangeEvent({
            objectType: 'RuleSet',
            changeType: 'Delete',
            context: trackingContext,
          });

          onSuccess();

          if (configParamKey) {
            const newQueryString = updateQueryString([], [configParamKey]);
            navigate(`?${newQueryString}`, { replace: true });
          }

          resetGenericDeleteModal();
        }
      }}
    />
  );
};

export const GenericDeleteModal = ({
  configParamKey,
  onSuccess,
}: {
  configParamKey?: string;
  onSuccess?: () => void;
}) => {
  const isGenericDeleteModalOpen = useAtomValue(isGenericDeleteModalOpenAtom);

  return (
    isGenericDeleteModalOpen && (
      <ErrorBoundary fallback={<GenericDeleteModalBase feedback="error" />}>
        <Suspense fallback={<GenericDeleteModalBase feedback="loading" />}>
          <GenericDeleteModalWithData configParamKey={configParamKey} onSuccess={onSuccess} />
        </Suspense>
      </ErrorBoundary>
    )
  );
};
